import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { disabledStyle, roundValue } from '../utils';
import { MAX_DOWNPAYMENT_THRESHOLD } from '../../common/constants';
import { areStringsEqual, ENTER_KEY_CODE, getNewTarget } from '../../common/utils';
import { useIsAspPaymentGridRedesignActive } from '../hooks/useIsAspPaymentGridRedesignActive';
import type { IPaymentGridErrorState } from '@makemydeal/dr-dash-types';
import { INPUT_INVALID_ERROR_MESSAGE } from '../../../constants';

export interface DownPaymentInputFieldProps {
    downPaymentValue: number | null;
    rowIndex: number;
    isLoading?: boolean;
}

export const DownPaymentInputField = ({ downPaymentValue, rowIndex, isLoading }: DownPaymentInputFieldProps) => {
    const dispatch = useDispatch();
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const [downPaymentDisplay, setDownPaymentDisplay] = useState('');
    const [isFocused, setFocus] = useState(false);
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const resolvedSelectedRow = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedRowIndex);

    useEffect(() => {
        if (!isLoading) {
            const roundedStringValue =
                downPaymentValue !== null && Number.isFinite(downPaymentValue) ? `${roundValue(String(downPaymentValue))}` : '';
            setDownPaymentDisplay(roundedStringValue);
        }
    }, [downPaymentValue, isLoading]);

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => setDownPaymentDisplay(e.target.value);

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (_e) => {
        setFocus(true);
    };

    const handleBlur = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        const value = e.target.value;

        const valueNumber = value.toString().length ? Number(value) : null;

        const roundedValue = roundValue(value);

        setTimeout(() => {
            setDownPaymentDisplay(`${roundedValue ?? ''}`);
            setFocus(false);
        });

        if (downPaymentValue === valueNumber) {
            return;
        }

        logNewRelicPageAction('MV:PaymentGrid - Change cash down', {
            mvOfferType: offerType,
            mvFieldEdited: 'mv:payment:grid:cash-down',
            mvDealXgId: dealXgId
        });

        if (isAspPgRedesignEnabled && resolvedSelectedRow != null) {
            dispatch(offerReduxActionCreators.setPaymentGridSelectedRow(offerType, resolvedSelectedRow));
        }

        dispatch(
            offerReduxActionCreators.updatePaymentGridCashdown(
                offerType,
                rowIndex,
                valueNumber,
                CDL_USER_INTENT.PAYMENT_GRID_TEXT_ENTRY
            )
        );
        setFocus(false);
    };

    const handleKeyDownEvent = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === ENTER_KEY_CODE) {
            const newTarget = getNewTarget(event);
            handleBlur(newTarget);
        }
    };

    return (
        <NumericInput
            autoInsertCommas
            allowNegative={false}
            id={`grid-cashdown-input-${rowIndex}`}
            data-testid={`grid-cashdown-input-${rowIndex}`}
            maxValue={MAX_DOWNPAYMENT_THRESHOLD}
            sx={{
                ...(isLoading && disabledStyle)
            }}
            allowDecimal={true}
            decimalMaxLength={2}
            inputPrefix={'$'}
            hasError={
                areStringsEqual(paymentGridErrorState.lastUpdatedInput, `input_cash_${rowIndex}`) && paymentGridErrorState.hasError
            }
            errorMessage={INPUT_INVALID_ERROR_MESSAGE}
            customErrorMessages={{ exceedsMaxValueRule: INPUT_INVALID_ERROR_MESSAGE }}
            name={`cashdown-input-${rowIndex}`}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDownEvent}
            value={isFocused ? String(downPaymentValue ?? '') : downPaymentDisplay}
            size="sm"
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
        />
    );
};
